import type { ImperativePanelHandle } from "react-resizable-panels";

import { Paper, ScrollArea } from "@mantine/core";
import { type ReactNode, useMemo, useRef } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";

import type { AsidePanelConfig, MainPanelConfig, TopbarConfig } from "@/panels/modules/panels/store/panels.store.types";

import { MainPanel } from "@/panels/modules/panels/components/MainPanel";
import { PanelErrorFallback } from "@/panels/modules/panels/components/PanelErrorFallback";
import {
    calculateLeftPanelSize,
    computePanelSizes,
    computePanelStates,
} from "@/panels/modules/panels/components/PanelsView.helpers";
import { usePanelCollapse } from "@/panels/modules/panels/hooks/usePanelCollapse";
import { usePanelResize } from "@/panels/modules/panels/hooks/usePanelResize";
import { usePersistPanelSize } from "@/panels/modules/panels/hooks/usePersistPanelSize";
import { useVerticalPanelPairResize } from "@/panels/modules/panels/hooks/useVerticalPanelPairResize";

import styles from "./PanelsView.module.css";

const LEFT_MAIN_PANEL_GROUP_ID = "leftMainPanelGroup";
const RIGHT_MAIN_PANEL_GROUP_ID = "rightMainPanelGroup";
const LOCK_ASIDE_PANELS = true;
const LOCK_MAIN_PANELS = false;

interface PanelsViewProps {
    // Content slots
    leftSidebar?: ReactNode;
    topbar?: ReactNode;
    rightSidebar?: ReactNode;
    leftTopA?: ReactNode;
    leftTopB?: ReactNode;
    leftBottomA?: ReactNode;
    leftBottomB?: ReactNode;
    rightTopA?: ReactNode;
    rightTopB?: ReactNode;
    rightBottomA?: ReactNode;
    rightBottomB?: ReactNode;

    // Main panel configs
    leftSidebarConfig: AsidePanelConfig;
    topbarConfig: TopbarConfig;
    rightSidebarConfig: AsidePanelConfig;
    leftTopConfig: MainPanelConfig;
    leftBottomConfig: MainPanelConfig;
    rightTopConfig: MainPanelConfig;
    rightBottomConfig: MainPanelConfig;

    // Global panel configs
    leftMainPanelDefaultSize: number;
}

export const PanelsView = ({
    leftSidebar,
    topbar,
    rightSidebar,
    leftTopA,
    leftTopB,
    leftBottomA,
    leftBottomB,
    rightTopA,
    rightTopB,
    rightBottomA,
    rightBottomB,
    leftSidebarConfig,
    topbarConfig,
    rightSidebarConfig,
    leftTopConfig,
    leftBottomConfig,
    rightTopConfig,
    rightBottomConfig,
    leftMainPanelDefaultSize,
}: PanelsViewProps) => {
    const sizes = useMemo(
        () => computePanelSizes({ topbarConfig, leftSidebarConfig, rightSidebarConfig }),
        [topbarConfig, leftSidebarConfig, rightSidebarConfig],
    );

    const leftAsidePanelRef = useRef<ImperativePanelHandle>(null);
    const topbarPanelRef = useRef<ImperativePanelHandle>(null);
    const rightAsidePanelRef = useRef<ImperativePanelHandle>(null);
    const leftMainPanelGroupRef = useRef<ImperativePanelHandle>(null);
    const rightMainPanelGroupRef = useRef<ImperativePanelHandle>(null);
    const leftTopPanelRef = useRef<ImperativePanelHandle>(null);
    const leftBottomPanelRef = useRef<ImperativePanelHandle>(null);
    const rightTopPanelRef = useRef<ImperativePanelHandle>(null);
    const rightBottomPanelRef = useRef<ImperativePanelHandle>(null);

    // Compute all panel states and collapsed heights
    const {
        isOneLeftMainPanelFullyClosedValue,
        isOneRightMainPanelFullyClosedValue,
        isOneSideOfMainPanelsFullyClosedValue,
        leftTopCollapsedHeightPixels,
        rightTopCollapsedHeightPixels,
        leftBottomCollapsedHeightPixels,
        rightBottomCollapsedHeightPixels,
    } = computePanelStates({
        leftTopConfig,
        leftBottomConfig,
        rightTopConfig,
        rightBottomConfig,
        leftTopA,
        leftTopB,
        leftBottomA,
        leftBottomB,
        rightTopA,
        rightTopB,
        rightBottomA,
        rightBottomB,
    });

    usePanelCollapse({
        panelRef: leftAsidePanelRef,
        isCollapsed: leftSidebarConfig.isCollapsed,
    });

    usePanelCollapse({
        panelRef: topbarPanelRef,
        isCollapsed: topbarConfig.isCollapsed,
    });

    usePanelCollapse({
        panelRef: rightAsidePanelRef,
        isCollapsed: rightSidebarConfig.isCollapsed,
    });

    const { size: lastLeftMainPanelGroupSize, handleResize: handleLeftMainPanelGroupResize } = usePersistPanelSize({
        initialSize: leftMainPanelDefaultSize,
    });

    usePanelResize({
        panelRef: leftMainPanelGroupRef,
        size: calculateLeftPanelSize({
            lastSize: lastLeftMainPanelGroupSize,
            leftTopAIsClosed: leftTopConfig.a.isClosed,
            leftTopBIsClosed: leftTopConfig.b.isClosed,
            leftBottomAIsClosed: leftBottomConfig.a.isClosed,
            leftBottomBIsClosed: leftBottomConfig.b.isClosed,
            rightTopAIsClosed: rightTopConfig.a.isClosed,
            rightTopBIsClosed: rightTopConfig.b.isClosed,
            rightBottomAIsClosed: rightBottomConfig.a.isClosed,
            rightBottomBIsClosed: rightBottomConfig.b.isClosed,
            leftTopA,
            leftTopB,
            leftBottomA,
            leftBottomB,
            rightTopA,
            rightTopB,
            rightBottomA,
            rightBottomB,
        }),
        dependencies: [
            leftTopConfig.a.isClosed,
            leftTopConfig.b.isClosed,
            leftBottomConfig.a.isClosed,
            leftBottomConfig.b.isClosed,
            rightTopConfig.a.isClosed,
            rightTopConfig.b.isClosed,
            rightBottomConfig.a.isClosed,
            rightBottomConfig.b.isClosed,
            leftTopA,
            leftTopB,
            leftBottomA,
            leftBottomB,
            rightTopA,
            rightTopB,
            rightBottomA,
            rightBottomB,
        ],
    });

    const { size: lastLeftTopPanelSize, handleResize: handleLeftTopPanelResize } = usePersistPanelSize();
    const { isBottomMinimizedOrClosed: isLeftBottomMinimizedOrClosed } = useVerticalPanelPairResize({
        topConfig: leftTopConfig,
        bottomConfig: leftBottomConfig,
        topASlot: leftTopA,
        topBSlot: leftTopB,
        bottomASlot: leftBottomA,
        bottomBSlot: leftBottomB,
        lastTopPanelSize: lastLeftTopPanelSize,
        topPanelRef: leftTopPanelRef,
        bottomPanelRef: leftBottomPanelRef,
    });

    const { size: lastRightTopPanelSize, handleResize: handleRightTopPanelResize } = usePersistPanelSize();
    const { isBottomMinimizedOrClosed: isRightBottomMinimizedOrClosed } = useVerticalPanelPairResize({
        topConfig: rightTopConfig,
        bottomConfig: rightBottomConfig,
        topASlot: rightTopA,
        topBSlot: rightTopB,
        bottomASlot: rightBottomA,
        bottomBSlot: rightBottomB,
        lastTopPanelSize: lastRightTopPanelSize,
        topPanelRef: rightTopPanelRef,
        bottomPanelRef: rightBottomPanelRef,
    });

    return (
        <PanelGroup direction="horizontal">
            <Panel
                ref={leftAsidePanelRef}
                defaultSize={sizes.leftSidebar.defaultWidth}
                collapsible={true}
                style={{
                    minWidth: leftSidebarConfig.isCollapsed ? undefined : sizes.leftSidebar.minWidth,
                    maxWidth: leftSidebarConfig.isCollapsed ? undefined : sizes.leftSidebar.maxWidth,
                }}
            >
                <Paper radius={0} h="100%" className={styles.panelContainer}>
                    <ScrollArea className={styles.scrollArea}>
                        <ErrorBoundary fallbackRender={PanelErrorFallback}>{leftSidebar}</ErrorBoundary>
                    </ScrollArea>
                </Paper>
            </Panel>

            <PanelResizeHandle
                disabled={LOCK_ASIDE_PANELS}
                hitAreaMargins={{
                    coarse: 8,
                    fine: 4,
                }}
                className={`${styles.panelResizeHandle} ${styles.panelResizeHandleHorizontal}`}
            />

            <Panel className={styles.mainPanelContainer}>
                <PanelGroup direction={"vertical"}>
                    <Panel
                        ref={topbarPanelRef}
                        collapsible={true}
                        defaultSize={sizes.topbar.defaultHeight}
                        style={{
                            minHeight: topbarConfig.isCollapsed ? undefined : sizes.topbar.minHeight,
                            maxHeight: topbarConfig.isCollapsed ? undefined : sizes.topbar.maxHeight,
                        }}
                    >
                        <Paper radius={0} h="100%" className={styles.panelContainer}>
                            <ScrollArea className={styles.scrollArea}>
                                <ErrorBoundary fallbackRender={PanelErrorFallback}>{topbar}</ErrorBoundary>
                            </ScrollArea>
                        </Paper>
                    </Panel>

                    <PanelResizeHandle
                        disabled={LOCK_ASIDE_PANELS}
                        hitAreaMargins={{
                            coarse: 8,
                            fine: 4,
                        }}
                        className={`${styles.panelResizeHandle} ${styles.panelResizeHandleVertical}`}
                    />

                    <Panel minSize={sizes.mainPanels.minSize}>
                        <PanelGroup direction="horizontal">
                            <Panel
                                ref={leftMainPanelGroupRef}
                                id={LEFT_MAIN_PANEL_GROUP_ID}
                                collapsedSize={0}
                                collapsible={isOneSideOfMainPanelsFullyClosedValue}
                                minSize={sizes.mainPanels.minSize}
                                onResize={handleLeftMainPanelGroupResize}
                            >
                                <PanelGroup direction="vertical">
                                    <Panel
                                        id="leftTop"
                                        ref={leftTopPanelRef}
                                        collapsible={true}
                                        minSize={sizes.mainPanels.minSize}
                                        style={{
                                            // Limit the collapsed height to make room for the header and tabs, if present.
                                            flexBasis: leftTopCollapsedHeightPixels,
                                        }}
                                        onResize={handleLeftTopPanelResize}
                                    >
                                        <MainPanel panelId={"leftTop"} tabASlot={leftTopA} tabBSlot={leftTopB} />
                                    </Panel>

                                    <PanelResizeHandle
                                        hitAreaMargins={{
                                            coarse: 8,
                                            fine: 4,
                                        }}
                                        disabled={LOCK_MAIN_PANELS || isOneLeftMainPanelFullyClosedValue}
                                        className={`${styles.panelResizeHandle} ${styles.panelResizeHandleVertical} ${isOneLeftMainPanelFullyClosedValue ? styles.panelResizeHandleHidden : ""}`}
                                    />

                                    <Panel
                                        id="leftBottom"
                                        ref={leftBottomPanelRef}
                                        collapsible={true}
                                        minSize={sizes.mainPanels.minSize}
                                        style={{
                                            flexBasis: leftBottomCollapsedHeightPixels,
                                            ...(isLeftBottomMinimizedOrClosed && {
                                                maxHeight: leftBottomCollapsedHeightPixels,
                                            }),
                                        }}
                                    >
                                        <MainPanel
                                            panelId={"leftBottom"}
                                            tabASlot={leftBottomA}
                                            tabBSlot={leftBottomB}
                                        />
                                    </Panel>
                                </PanelGroup>
                            </Panel>

                            <PanelResizeHandle
                                hitAreaMargins={{
                                    coarse: 8,
                                    fine: 4,
                                }}
                                disabled={LOCK_MAIN_PANELS || isOneSideOfMainPanelsFullyClosedValue}
                                className={`${styles.panelResizeHandle} ${!isOneSideOfMainPanelsFullyClosedValue && styles.panelResizeHandleHorizontal}`}
                            />

                            <Panel
                                ref={rightMainPanelGroupRef}
                                id={RIGHT_MAIN_PANEL_GROUP_ID}
                                collapsedSize={0}
                                collapsible={isOneSideOfMainPanelsFullyClosedValue}
                                minSize={sizes.mainPanels.minSize}
                            >
                                <PanelGroup direction="vertical">
                                    <Panel
                                        id="rightTop"
                                        ref={rightTopPanelRef}
                                        collapsible={true}
                                        minSize={sizes.mainPanels.minSize}
                                        style={{
                                            // Limit the collapsed height to make room for the header and tabs, if present.
                                            flexBasis: rightTopCollapsedHeightPixels,
                                        }}
                                        onResize={handleRightTopPanelResize}
                                    >
                                        <MainPanel panelId={"rightTop"} tabASlot={rightTopA} tabBSlot={rightTopB} />
                                    </Panel>

                                    <PanelResizeHandle
                                        hitAreaMargins={{
                                            coarse: 8,
                                            fine: 4,
                                        }}
                                        disabled={LOCK_MAIN_PANELS || isOneRightMainPanelFullyClosedValue}
                                        className={`${styles.panelResizeHandle} ${styles.panelResizeHandleVertical} ${isOneRightMainPanelFullyClosedValue ? styles.panelResizeHandleHidden : ""}`}
                                    />

                                    <Panel
                                        id="rightBottom"
                                        ref={rightBottomPanelRef}
                                        collapsible={true}
                                        minSize={sizes.mainPanels.minSize}
                                        style={{
                                            // Limit the collapsed height to make room for the header and tabs, if present.
                                            flexBasis: rightBottomCollapsedHeightPixels,
                                            // When minimized, cap height so only the header shows.
                                            ...(isRightBottomMinimizedOrClosed && {
                                                maxHeight: rightBottomCollapsedHeightPixels,
                                            }),
                                        }}
                                    >
                                        <MainPanel
                                            panelId={"rightBottom"}
                                            tabASlot={rightBottomA}
                                            tabBSlot={rightBottomB}
                                        />
                                    </Panel>
                                </PanelGroup>
                            </Panel>
                        </PanelGroup>
                    </Panel>
                </PanelGroup>
            </Panel>

            <PanelResizeHandle
                disabled={LOCK_ASIDE_PANELS}
                hitAreaMargins={{
                    coarse: 8,
                    fine: 4,
                }}
                className={`${styles.panelResizeHandle} ${styles.panelResizeHandleHorizontal}`}
            />

            <Panel
                ref={rightAsidePanelRef}
                defaultSize={sizes.rightSidebar.defaultWidth}
                collapsible={true}
                style={{
                    minWidth: rightSidebarConfig.isCollapsed ? undefined : sizes.rightSidebar.minWidth,
                    maxWidth: rightSidebarConfig.isCollapsed ? undefined : sizes.rightSidebar.maxWidth,
                }}
            >
                <Paper radius={0} h={"100%"} className={styles.panelContainer}>
                    <ScrollArea className={styles.scrollArea}>
                        <ErrorBoundary fallbackRender={PanelErrorFallback}>{rightSidebar}</ErrorBoundary>
                    </ScrollArea>
                </Paper>
            </Panel>
        </PanelGroup>
    );
};
